import { StatusBadge } from './ui';
import type { DeploymentImpact, DeploymentLatency, DeploymentVerification } from '../api';
import { Callout } from './primitives';
import { cacheActionLabel, cacheLayerLabel, cacheProviderLabel, humanize, labelFor, verificationCheckLabel, verificationScopeLabel, statusFor } from '../labels';

/**
 * DeploymentImpactPanel shows the blast radius of a deployment or a rollback
 * (CO-46): which websites, hostnames and frontends it touches, and which
 * caches are flushed by it or must be purged by hand afterwards. Shown
 * before a deployment starts and again on its detail page.
 */
export function DeploymentImpactPanel({ impact, heading, rollback = false }: { impact: DeploymentImpact | null | undefined; heading?: string; rollback?: boolean }) {
  if (!impact) return null;
  const verb = rollback ? 'Rolling back' : 'This deployment';
  return (
    <div className="impact" data-testid="deployment-impact" aria-live="polite">
      <h3 className="impact__title">{heading ?? (impact.installation ? `${verb} affects every website of this Magento installation` : `${verb} affects one storefront`)}</h3>
      {impact.installation ? (
        <>
          {impact.websites.length > 0 ? (
            <ul className="impact__list">
              {impact.websites.map((w) => (
                <li key={w.website_id}>
                  <strong>{w.name || w.code}</strong>
                  <code>{w.code}</code>
                  {w.domains.length > 0 ? <span>{w.domains.join(', ')}</span> : <span className="muted">no hostname bound</span>}
                  {w.store_views.length > 0 ? <span className="muted">store view{w.store_views.length === 1 ? '' : 's'} {w.store_views.join(', ')}</span> : null}
                  {w.frontends.length > 0 ? <span className="muted">frontend {w.frontends.join(', ')}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="type-description">No websites discovered · run <em>Discover in Magento</em> on the storefront map</p>
          )}
        </>
      ) : null}
      {impact.frontends.length > 0 ? (
        <ul className="impact__list">
          {impact.frontends.map((f) => (
            <li key={f.application_id}>
              <strong>{f.name}</strong>
              {f.domains.length > 0 ? <span>{f.domains.join(', ')}</span> : <span className="muted">no hostname bound</span>}
              {f.website_code ? <span className="muted">website {f.website_code}</span> : null}
              {f.store_view_code ? <span className="muted">store view {f.store_view_code}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {impact.cache_layers.length > 0 ? (
        <details className="disclosure">
          <summary>Caches</summary>
          <ul className="impact__list">
            {impact.cache_layers.map((c, i) => (
              <li key={`${c.layer}-${i}`}>
                <span>{labelFor(cacheLayerLabel, c.layer)}</span>
                <span className="cell-inline">
                  <StatusBadge kind="cacheAction" value={c.action} title={labelFor(cacheActionLabel, c.action)} />
                  {c.provider ? <span className="muted">{labelFor(cacheProviderLabel, c.provider)}</span> : null}
                  {c.targets && c.targets.length > 0 ? <span className="muted">{c.targets.join(', ')}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

interface VerificationGroup {
  key: string;
  title: string;
  kind: string;
  rows: DeploymentVerification[];
}

/**
 * groupVerifications folds a deployment's verification results into one
 * group per website (plus the backend), so a failing website reads as
 * "mytherm.ro FAIL" next to "cazane-shop.ro PASS" rather than a flat list.
 */
export function groupVerifications(verifications: DeploymentVerification[]): VerificationGroup[] {
  const groups = new Map<string, VerificationGroup>();
  const add = (key: string, title: string, kind: string, v: DeploymentVerification) => {
    const g = groups.get(key) ?? { key, title, kind, rows: [] };
    g.rows.push(v);
    groups.set(key, g);
  };
  for (const v of verifications) {
    const s = v.scope;
    if (!s || s.kind === 'installation') {
      add('installation', 'Magento backend', 'installation', v);
    } else if (s.website_code) {
      add(`website:${s.website_code}`, s.website_code, 'website', v);
    } else if (s.frontend_name) {
      add(`frontend:${s.frontend_name}`, s.frontend_name, 'frontend', v);
    } else {
      add(`other:${s.label}`, s.label, s.kind, v);
    }
  }
  const order = (g: VerificationGroup) => (g.kind === 'installation' ? 0 : 1);
  return [...groups.values()].sort((a, b) => order(a) - order(b) || a.title.localeCompare(b.title));
}

function groupStatus(rows: DeploymentVerification[]): 'passed' | 'failed' | 'warning' {
  if (rows.some((r) => r.status === 'failed')) return 'failed';
  if (rows.some((r) => r.status === 'warning')) return 'warning';
  return 'passed';
}

export function VerificationGroups({ verifications }: { verifications: DeploymentVerification[] }) {
  const groups = groupVerifications(verifications);
  const failing = groups.filter((g) => groupStatus(g.rows) === 'failed');
  return (
    <div className="verification-groups" data-testid="verification-groups">
      {failing.length > 0 ? (
        <Callout tone="warning">
          {failing.length === 1 ? `${failing[0]!.title} failed its checks` : `${failing.length} storefronts failed their checks`}; the others passed.
        </Callout>
      ) : null}
      {groups.map((g) => {
        const st = groupStatus(g.rows);
        return (
          <section key={g.key} aria-label={`${g.title} verification`} data-testid={`verification-group-${g.key}`}>
            <h5 className="verification-group__title">
              {g.kind === 'installation' ? 'Magento backend' : g.kind === 'frontend' ? `Frontend ${g.title}` : g.title}
              <StatusBadge kind="checkResult" value={st} />
              {g.kind !== 'installation' ? <span className="type-meta">{labelFor(verificationScopeLabel, g.kind)}</span> : null}
            </h5>
            <ul className="verification-group__rows">
              {g.rows.map((v) => {
                const label = v.scope ? `${labelFor(verificationCheckLabel, v.scope.check)}${v.scope.kind === 'store_view' && v.scope.store_view_code ? ` (${v.scope.store_view_code})` : ''}${v.scope.hostname && v.scope.kind !== 'installation' && v.scope.kind !== 'website' ? ` · ${v.scope.hostname}` : ''}` : humanize(v.verification_type);
                const err = typeof v.details?.error === 'string' ? v.details.error : undefined;
                return (
                  <li key={v.id}>
                    <span>
                      {label}
                      {err ? <span className="fact-block__sub code--wrap">{err}</span> : null}
                    </span>
                    <StatusBadge
                      kind="checkResult"
                      value={v.status === 'passed' || v.status === 'failed' ? v.status : 'warning'}
                      label={`${statusFor('checkResult', v.status === 'passed' || v.status === 'failed' ? v.status : 'warning').label}${v.latency_ms > 0 ? ` · ${v.latency_ms} ms` : ''}`}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

/** How the percentage reads: +125 %, or -30 % when the site got faster. */
export function increaseLabel(latency: Pick<DeploymentLatency, 'increase_percent'>): string {
  const p = latency.increase_percent;
  return `${p > 0 ? '+' : ''}${p} %`;
}

/**
 * The speed check of a deployment (CO-198): the median answer time of the
 * storefront's own checks before the deployment started against after it
 * went live. Numbers, not a paragraph; "not enough checks" is never shown
 * as fine.
 */
export function SpeedCheck({ latency }: { latency: DeploymentLatency }) {
  const { state, before, after } = latency;
  const measured = state === 'ok' || state === 'slower';
  return (
    <section aria-label="Storefront speed" data-testid="speed-check">
      <h5 className="verification-group__title">
        Storefront speed
        <StatusBadge kind="speedCheck" value={state} />
      </h5>
      <ul className="verification-group__rows">
        <li>
          <span>
            Median answer time
            <span className="fact-block__sub">
              {before.samples} checks before · {after.samples} after
            </span>
          </span>
          {measured ? (
            <span data-testid="speed-check-values">
              {before.median_ms} ms → {after.median_ms} ms ({increaseLabel(latency)})
            </span>
          ) : state === 'measuring' && latency.ready_at ? (
            <span className="muted">Ready {new Date(latency.ready_at).toLocaleTimeString()}</span>
          ) : null}
        </li>
      </ul>
    </section>
  );
}
